import { initContract } from "@ts-rest/core";
import { FeatureCollection, Polygon } from "geojson";
import { z } from "zod";

import {
  AvailabilityReportStatusSchema,
  AvailabilitySchema,
  MemberSummarySchema,
  PaginationQueryParamsSchema,
  ServerDateSchema,
  getPaginationResponseSchema,
} from "./common";
import { MemberSchema } from "./members";

const c = initContract();

const AggregatedReportingAvailabilityPeriod = z.object({
  period: z.string(),
  immediatelyAvailable: z.number(),
  available: z.number(),
  conditional: z.number(),
  unavailable: z.number(),
  committed: z.number(),
  unset: z.number(),
  lowAvailability: z.boolean(),
});

const IndividualReportingAvailabilitySchema = z.object({
  member: MemberSummarySchema,
  availability: z.array(AvailabilitySchema),
});

const UnitAvailabilitySchema = z.object({
  unitId: z.string(),
  immediatelyAvailable: z.number(),
  available: z.number(),
  conditional: z.number(),
  unavailable: z.number(),
  committed: z.number(),
  unset: z.number(),
  totalMembers: z.number(),
});

const AvailabilitySummarySchema = z.object({
  activeRequestsCount: z.number(),
  activatedMembersCount: z.number(),
  availableMembersCount: z.number(),
});

const ActiveRequestSchema = z.object({
  id: z.string(),
  unitId: z.string(),
  type: z.enum(["activation", "activity", "out-of-area-activation"]),
  title: z.string(),
  location: z.string().optional(),
  latitude: z.number().optional(),
  longitude: z.number().optional(),
});

export type UnitPolygonProperties = {
  unitId: string;
  unitCode: string;
  unitName: string;
};

const reportingContract = c.router({
  getAggregatedAvailability: {
    method: "GET",
    path: "/reporting/availability/aggregated",
    query: z.object({
      startDate: ServerDateSchema,
      endDate: ServerDateSchema,
      groupId: z.string().optional(),
      collectionId: z.coerce.string().optional(),
      collectionType: z.enum(["state", "zone", "cluster", "unit"]),
      capabilityId: z.string().optional(),
      memberType: z.enum(["Volunteer", "Staff"]).optional(),
    }),
    responses: {
      200: z.array(AggregatedReportingAvailabilityPeriod),
    },
    summary: "Get the aggregate availability of a collection",
  },
  getMembersByAvailability: {
    method: "GET",
    path: "/reporting/availability/members",
    query: PaginationQueryParamsSchema.extend({
      startDate: ServerDateSchema.optional(),
      endDate: ServerDateSchema.optional(),
      groupId: z.string().optional(),
      availabilityReportStatus: AvailabilityReportStatusSchema.optional(),
      collectionId: z.coerce.string().optional(),
      collectionType: z.enum(["state", "zone", "cluster", "unit"]),
      capabilityId: z.string().optional(),
      memberType: z.enum(["Volunteer", "Staff"]).optional(),
    }),
    responses: {
      200: getPaginationResponseSchema(MemberSchema),
    },
    summary: "Get members by availability period and status",
  },
  getAvailabilitySummary: {
    method: "GET",
    path: "/reporting/availability/summary",
    query: z.object({
      startDate: ServerDateSchema.optional(),
    }),
    responses: {
      200: AvailabilitySummarySchema,
    },
    summary: "Get unit availability summary",
  },
  listUnitAvailability: {
    method: "GET",
    path: "/reporting/units/availability",
    query: z.object({
      startDate: ServerDateSchema.optional(),
    }),
    responses: {
      200: z.array(UnitAvailabilitySchema),
    },
    summary: "List unit availability",
  },
  listUnitPolygons: {
    method: "GET",
    path: "/reporting/units/polygons",
    responses: {
      200: c.type<FeatureCollection<Polygon, UnitPolygonProperties>>(),
    },
    summary: "List unit polygons",
  },
  listActiveRequests: {
    method: "GET",
    path: "/reporting/active-requests",
    responses: {
      200: z.array(ActiveRequestSchema),
    },
    summary: "List active requests",
  },
});

export type AggreggatedReportingAvailabilityPeriod = z.infer<
  typeof AggregatedReportingAvailabilityPeriod
>;

export type IndividualReportingAvailability = z.infer<
  typeof IndividualReportingAvailabilitySchema
>;

export type UnitAvailability = z.infer<typeof UnitAvailabilitySchema>;

export type ActiveRequest = z.infer<typeof ActiveRequestSchema>;

export { reportingContract };
